import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { createClient } from '@propelauth/javascript';
import { Experiments } from './components/Experiments';
import { ICPExplorer } from './components/ICPExplorer';
import { ICPIdeation } from './components/ICPIdeation';
import { LinkTracker } from './components/LinkTracker';
import RankAList from './pages/RankAList';
import { Settings } from './components/Settings';
import { Documentation } from './components/Documentation';
import { Button } from './components/ui/button';
import { Toaster } from './components/ui/toaster';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './components/ui/dropdown-menu';
import { User, ChevronDown } from 'lucide-react';

// Initialize PropelAuth
const authUrl = 'https://10835339.propelauthtest.com';

interface User {
  userId: string;
  email: string;
  // Add other user properties as needed
}

// Sidebar component that uses router
const Sidebar: React.FC = () => {
  const location = useLocation();
  
  const isActive = (path: string) => {
    if (path === '/' || path === '/experiments') {
      return location.pathname === '/' || location.pathname.startsWith('/experiments');
    }
    return location.pathname.startsWith(path);
  };

  const navigationItems = [
    { path: '/experiments', label: '🧪 Experiments' },
    { path: '/icp-explorer', label: '🎯 ICP Ideation' },
    { path: '/rank-a-list', label: '📊 Rank a List' },
    { path: '/link-tracker', label: '🔗 Link Tracker' },
    { path: '/settings', label: '⚙️ Settings' },
  ];

  return (
    <nav className="w-64 bg-card border-r border-border p-4">
      <div className="mb-6">
        <h2 className="text-lg font-semibold">Navigation</h2>
      </div>
      <ul className="space-y-2">
        {navigationItems.map((item) => (
          <li key={item.path}>
            <Link
              to={item.path}
              className={`block w-full text-left px-4 py-2 rounded-md transition-colors ${
                isActive(item.path)
                  ? 'bg-primary text-primary-foreground font-semibold'
                  : 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
              }`}
            >
              {item.label}
            </Link>
          </li>
        ))}
      </ul>

      {/* Resources */}
      <div className="mt-8 pt-4 border-t border-border">
        <h3 className="text-sm font-medium text-muted-foreground mb-3 px-4">Resources</h3>
        <ul className="space-y-1">
          <li>
            <Link
              to="/documentation"
              className="flex items-center w-full text-left px-4 py-2 rounded-md transition-colors text-muted-foreground hover:bg-accent hover:text-accent-foreground"
            >
              📚 Python Client
            </Link>
          </li>
        </ul>
      </div>
    </nav>
  );
};

// Main app content with routing
const AppContent: React.FC<{ propelauth: any; user: User }> = ({ propelauth, user }) => {
  const navigate = useNavigate();

  const handleLogout = () => {
    propelauth.logout().then(() => {
      navigate('/');
    });
  };

  const handlePageNavigation = (page: string) => {
    navigate(`/${page}`);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="bg-card border-b border-border px-4 py-2 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/">
            <img src="/images/alphaloop_logo_inverse.png" alt="AlphaLoop" className="h-[72px]" />
          </Link>
        </div>
        
        {/* User Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" className="flex items-center gap-2">
              <User className="h-4 w-4" />
              <span className="hidden sm:inline">{user.email}</span>
              <ChevronDown className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem onClick={handleLogout} className="text-red-600 focus:text-red-600">
              Logout
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </header>

      {/* Main Content */}
      <div className="flex flex-1">
        <Sidebar />
        
        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          <Routes>
            <Route path="/" element={<Experiments propelauth={propelauth} user={user} onNavigateToPage={handlePageNavigation} />} />
            <Route path="/experiments" element={<Experiments propelauth={propelauth} user={user} onNavigateToPage={handlePageNavigation} />} />
            <Route path="/experiments/:experimentId" element={<Experiments propelauth={propelauth} user={user} onNavigateToPage={handlePageNavigation} />} />
            <Route path="/goals/:goalId" element={<Experiments propelauth={propelauth} user={user} onNavigateToPage={handlePageNavigation} />} />
            <Route path="/icp-explorer" element={<ICPIdeation />} />
            <Route path="/rank-a-list" element={<RankAList />} />
            <Route path="/link-tracker" element={<LinkTracker />} />
            <Route path="/settings" element={<Settings propelauth={propelauth} />} />
            <Route path="/documentation" element={<Documentation />} />
          </Routes>
        </main>
      </div>
      <Toaster />
    </div>
  );
};

function App() {
  const [propelauth] = useState(() => createClient({ authUrl }));
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check authentication state
    propelauth.getAuthenticationInfoOrNull()
      .then((authInfo: any) => {
        if (authInfo && authInfo.user) {
          setUser(authInfo.user);
        }
        setIsLoading(false);
      })
      .catch((err: any) => {
        console.error("Error checking auth state:", err);
        setIsLoading(false);
      });
  }, [propelauth]);

  const handleLogin = () => {
    propelauth.redirectToLoginPage();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Welcome to AlphaLoop</h1>
          <p className="text-muted-foreground mb-6">Please log in to continue.</p>
          <Button onClick={handleLogin}>Login with PropelAuth</Button>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <AppContent propelauth={propelauth} user={user} />
    </Router>
  );
}

export default App; 